/**
 * synkro import — backfill historical Claude Code sessions for THIS repo.
 *
 * Walks ~/.claude/projects/<sanitized-cwd>/*.jsonl (every past session in the
 * repo), parses each turn (user/assistant content + per-turn usage + tool calls),
 * and ships them to storage so the dashboard can show every historical session
 * line-by-line.
 *
 *   • CLOUD mode  → ships to the API /api/v1/cli/sync-transcripts (automatic).
 *   • LOCAL mode  → on-device only, and CONSENT-GATED: we prompt before importing
 *     since it ingests the repo's full transcript history.
 *
 * Idempotent: message ids are deterministic (session:seq), so re-running is safe.
 */
import { existsSync, readFileSync, readdirSync } from 'node:fs';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { execSync } from 'node:child_process';
import { createInterface } from 'node:readline';
import { getAccessToken, ensureValidToken } from '../auth/stub.js';

const CONFIG_PATH = join(homedir(), '.synkro', 'config.env');

function readConfigEnv(): Record<string, string> {
  const out: Record<string, string> = {};
  try {
    for (const line of readFileSync(CONFIG_PATH, 'utf-8').split('\n')) {
      const t = line.trim();
      if (!t || t.startsWith('#')) continue;
      const eq = t.indexOf('=');
      if (eq > 0) out[t.slice(0, eq).trim()] = t.slice(eq + 1).trim().replace(/^['"]|['"]$/g, '');
    }
  } catch {}
  return out;
}

function projectsFolder(): string | null {
  // CC stores transcripts in ~/.claude/projects/<cwd with '/' -> '-'>. The leading
  // slash already becomes the leading dash — do NOT prepend another '-'.
  const sanitized = process.cwd().replace(/\//g, '-');
  const dir = join(homedir(), '.claude', 'projects', sanitized);
  return existsSync(dir) ? dir : null;
}

function repoName(): string {
  // Use the git remote "owner/repo" so it matches the project the hook already
  // created (project_repos.full_name). A bare basename would create a duplicate.
  try {
    const url = execSync('git config --get remote.origin.url', { encoding: 'utf-8' }).trim();
    const m = url.match(/[:/]([^/:]+\/[^/]+?)(?:\.git)?$/);
    if (m) return m[1];
  } catch {}
  try { return execSync('git rev-parse --show-toplevel', { encoding: 'utf-8' }).trim().split('/').pop() || 'repo'; }
  catch { return process.cwd().split('/').pop() || 'repo'; }
}

function extractText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .map((b: any) => (typeof b === 'string' ? b : b?.type === 'text' ? (b.text || '') : ''))
      .filter(Boolean)
      .join('\n');
  }
  return '';
}

interface ParsedSession {
  cc_session_id: string;
  messages: Array<Record<string, unknown>>;
  actions: Array<Record<string, unknown>>;
}

function parseSession(filePath: string, sessionId: string): ParsedSession {
  const lines = readFileSync(filePath, 'utf-8').split('\n').filter(Boolean);
  const messages: Array<Record<string, unknown>> = [];
  const actions: Array<Record<string, unknown>> = [];
  let step = 0;
  for (let i = 0; i < lines.length; i++) {
    let e: any;
    try { e = JSON.parse(lines[i]); } catch { continue; }
    const kind = e.type === 'assistant' || e.message?.role === 'assistant' ? 'assistant'
      : e.type === 'user' || e.message?.role === 'user' ? 'user' : '';
    if (!kind) continue;
    const msgObj = e.message;
    const text = extractText(msgObj?.content ?? e.content);
    // Tool calls FIRST — a tool-only assistant turn (Edit/Bash/Read, no text) must
    // still be captured (it's most of a coding session); skipping on empty text
    // dropped both the turn and its tool calls.
    const blocks = kind === 'assistant' && Array.isArray(msgObj?.content) ? msgObj.content : [];
    const toolCalls = blocks
      .filter((b: any) => b?.type === 'tool_use')
      .map((b: any) => ({ name: b.name, input: JSON.stringify(b.input || {}).slice(0, 20000), id: b.id }));
    if (!text.trim() && toolCalls.length === 0) continue;
    const msg: Record<string, unknown> = {
      message_index: i, type: kind,
      content: (text.trim() ? text.slice(0, 8000) : '↳ ' + toolCalls.map((t: any) => t.name).join(', ')),
      timestamp: e.timestamp || null,
    };
    if (kind === 'assistant') {
      if (typeof msgObj?.model === 'string') msg.model = msgObj.model;
      const u = msgObj?.usage;
      if (u && typeof u === 'object') {
        msg.usage = {
          input_tokens: Number(u.input_tokens) || 0,
          output_tokens: Number(u.output_tokens) || 0,
          cache_creation_input_tokens: Number(u.cache_creation_input_tokens) || 0,
          cache_read_input_tokens: Number(u.cache_read_input_tokens) || 0,
        };
      }
      if (toolCalls.length) {
        msg.tool_calls = toolCalls;
        for (const t of toolCalls) actions.push({ step: ++step, tool: t.name, summary: '', file: null, outcome: null });
      }
    }
    messages.push(msg);
  }
  return { cc_session_id: sessionId, messages, actions };
}

function ask(q: string): Promise<boolean> {
  const rl = createInterface({ input: process.stdin, output: process.stdout });
  return new Promise((resolve) => rl.question(q, (a) => { rl.close(); resolve(/^y(es)?$/i.test(a.trim())); }));
}

export async function importCommand(): Promise<void> {
  const config = readConfigEnv();
  const isCloud = config.SYNKRO_DEPLOY_LOCATION === 'cloud' || config.SYNKRO_STORAGE_MODE === 'cloud';
  const repo = repoName();

  const dir = projectsFolder();
  if (!dir) { console.log('No Claude Code transcripts found for this repo (~/.claude/projects).'); return; }
  const files = readdirSync(dir).filter((f) => f.endsWith('.jsonl'));
  if (!files.length) { console.log('No sessions to import.'); return; }

  console.log(`Found ${files.length} session(s) for "${repo}" (${isCloud ? 'cloud' : 'local'} mode).`);

  // LOCAL: ingesting the full transcript history is consent-gated.
  if (!isCloud) {
    const ok = await ask(`Import all ${files.length} historical session(s) into your on-device store? [y/N] `);
    if (!ok) { console.log('Aborted — nothing imported.'); return; }
  }

  const sessions = files.map((f) => parseSession(join(dir, f), f.replace('.jsonl', '')))
    .filter((s) => s.messages.length > 0);
  const totalMsgs = sessions.reduce((n, s) => n + s.messages.length, 0);

  let ok = 0, fail = 0;
  if (isCloud) {
    await ensureValidToken();
    const token = getAccessToken();
    if (!token) { console.log('Not authenticated — run `synkro login` first.'); return; }
    const gateway = config.SYNKRO_GATEWAY_URL || 'https://api.synkro.sh';
    // One session per call so large sessions (up to the endpoint's per-session cap)
    // land fully without a giant multi-session payload.
    for (const s of sessions) {
      try {
        const r = await fetch(`${gateway}/api/v1/cli/sync-transcripts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ repo, sessions: [s] }),
          signal: AbortSignal.timeout(60000),
        });
        if (r.ok) { ok++; process.stdout.write('.'); } else { fail++; console.warn(`\n  ${String(s.cc_session_id).slice(0, 8)} failed: HTTP ${r.status}`); }
      } catch (e) { fail++; console.warn(`\n  ${String(s.cc_session_id).slice(0, 8)} error: ${(e as Error).message}`); }
    }
    process.stdout.write('\n');
  } else {
    const port = config.SYNKRO_MCP_PORT || '18931';
    for (const s of sessions) {
      try {
        const r = await fetch(`http://127.0.0.1:${port}/api/ingest`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ capture_type: 'transcript_sync', session_id: s.cc_session_id, repo, messages: s.messages, actions: s.actions }),
          signal: AbortSignal.timeout(15000),
        });
        if (r.ok) ok++; else fail++;
      } catch { fail++; }
    }
    if (fail && ok === 0) console.log('  (is the local container running? `synkro status`)');
  }

  console.log(`\n✓ Imported ${ok} session(s) (${totalMsgs} messages)${fail ? `, ${fail} failed` : ''}.`);
}
